import { useEffect, useState } from "react";
import type React from "react";
import { cn } from "../../lib/utils";
import { Box } from "./box";
import { GridSpinner, type GridSpinnerProps } from "./grid-spinner";
import { useDelayedFade } from "./skeleton";
import { Text } from "./text";

/** Ms between caption swaps when the caption is a pool. */
const DEFAULT_INTERVAL_MS = 2200;

export type LoadingStateProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  /**
   * One line under the glyph, or a pool it is drawn from. A pool cycles: a
   * new phrase every `interval`, picked at random and never the one just
   * shown, each arriving on the fade-in every mounting text in the system
   * uses. Omit for the bare glyph.
   */
  caption?: string | readonly string[];
  /** Ms between swaps when `caption` is a pool. */
  interval?: number;
  /**
   * Invisible for its first ~200ms, then fades in as one unit (the Skeleton
   * contract). For DECISION gates — a branch that may redirect or swap
   * trees — so the common fast path never shows anything.
   */
  delayed?: boolean;
  /** The glyph's Icon size token. */
  size?: GridSpinnerProps["size"];
  /** The glyph's pattern; orbit is the wait-shaped one. */
  animation?: GridSpinnerProps["animation"];
};

/* A random step of 1..length-1 lands anywhere but the current index. */
function pickNext(length: number, current: number): number {
  if (length < 2) return 0;
  const step = 1 + Math.floor(Math.random() * (length - 1));
  return (current + step) % length;
}

/**
 * The full-pane wait: a GridSpinner centered in whatever it fills, with an
 * optional caption beneath. The glyph is the `md` rung by default — this is
 * a page saying "one moment", not a hero — and the caption is the 12px muted
 * line. The two are one unit: `delayed` gates both together, and a caption
 * swap never moves the glyph.
 */
export function LoadingState({
  caption,
  interval = DEFAULT_INTERVAL_MS,
  delayed = false,
  size = "md",
  animation = "orbit",
  className,
  ...props
}: LoadingStateProps): React.ReactElement {
  const pool = typeof caption === "string" ? [caption] : caption ?? [];
  const poolSize = pool.length;
  const [index, setIndex] = useState(0);

  // Keyed on the pool's SIZE, not the array: a caller passing a fresh literal
  // each render must not restart the cycle every frame.
  useEffect(() => {
    if (poolSize < 2) return;
    const timer = setInterval(
      () => setIndex((current) => pickNext(poolSize, current)),
      interval,
    );
    return () => clearInterval(timer);
  }, [interval, poolSize]);

  const phrase = pool[index % Math.max(poolSize, 1)];
  const fade = useDelayedFade(delayed);

  return (
    <Box
      fill="flex"
      direction="column"
      align="center"
      justify="center"
      gap={12}
      className={cn(fade, className)}
      data-slot="loading-state"
      {...props}
    >
      {/* The caption's ink; GridSpinner's own color names stop at `subtle`. */}
      <GridSpinner
        animation={animation}
        className="text-muted-foreground"
        size={size}
      />
      {phrase !== undefined && (
        <Text
          key={phrase}
          className="animate-fade-in motion-reduce:animate-none"
          size="xs"
          variant="muted"
        >
          {phrase}
        </Text>
      )}
    </Box>
  );
}
